import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGridCopyMenu } from './gridCommon';
import StockSearchInput from './StockSearchInput';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  type ColDef,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getMainAxios } from '../utils/util';
import { paramBoxStyle, paramTitleStyle } from './quantCommon';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

// 한국주식 왕복 거래비용 (매도세+수수료+슬리피지) — 백엔드 RoundTripCost와 동일
const COST_PCT = 0.2;

interface DayInfo { d: string; buckets: number; prods: number; }
interface Summary {
  n: number; icNow: number | null; icFwd1: number | null;
  icFwd3: number | null; icFwd5: number | null;
  r2Now: number | null; r2Fwd1: number | null; icObiFwd1: number | null;
}
interface BinRow { bin: number; avgSignal: number | null; avgRetNow: number | null; avgRetFwd: number | null; n: number; }
interface Backtest {
  trades: number; longs: number; shorts: number;
  grossPct: number | null; netPct: number | null; hitRate: number | null;
  sharpe: number | null; grossTotalPct: number | null; netTotalPct: number | null;
}
interface SweepRow { thr: number; hold: number; trades: number; grossPct: number | null; netPct: number | null; hitRate: number | null; }
interface SignalRow {
  bucket: string; prodId: string; prodNm?: string; px: number | null; totVol: number | null;
  tiVol: number | null; tiCnt: number | null; obi1: number | null; obiW5: number | null;
  obiTot: number | null; spread: number | null; confirmed: number;
  fills: number | null; snaps: number | null; retFwd1: number | null;
}

const cardStyle: React.CSSProperties = {
  border: '1px solid #bfdbfe', borderRadius: '8px', backgroundColor: '#fff',
  padding: '12px', marginBottom: '12px',
};
const cardTitleStyle: React.CSSProperties = {
  fontSize: '13px', fontWeight: 700, color: '#1e40af', marginBottom: '10px',
};
const labelStyle: React.CSSProperties = { fontSize: '12px', color: '#374151', marginRight: '6px' };
const inputStyle: React.CSSProperties = {
  fontSize: '12px', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '3px',
};
const btnStyle: React.CSSProperties = {
  padding: '5px 14px', borderRadius: '3px', border: 'none',
  backgroundColor: '#1e40af', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
};

const fmt = (v: number | null | undefined, d = 4) =>
  v === null || v === undefined ? '-' : Number(v).toFixed(d);

const QuantMicroSignal: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const axiosInstance = getMainAxios();

  const [days, setDays] = useState<DayInfo[]>([]);
  const [cfg, setCfg] = useState({
    d: '', prodId: '', thr: 0.25, hold: 1, maxSpreadBp: 20, confirmedOnly: true, limit: 500,
  });
  const [summary, setSummary] = useState<Summary | null>(null);
  const [bins, setBins] = useState<BinRow[]>([]);
  const [bt, setBt] = useState<Backtest | null>(null);
  const [sweep, setSweep] = useState<SweepRow[]>([]);
  const [signals, setSignals] = useState<SignalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ran, setRan] = useState(false);

  // 보유 데이터 일자 목록
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/api/v1/quant/micro/days');
        const list: DayInfo[] = res.data || [];
        setDays(list);
        if (list.length > 0) setCfg((p) => ({ ...p, d: list[0].d.split('T')[0] }));
      } catch (e) { console.error('일자 조회 실패:', e); }
    })();
  }, []);

  const run = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params: any = {
        thr: cfg.thr, hold: cfg.hold, maxSpreadBp: cfg.maxSpreadBp,
        confirmedOnly: cfg.confirmedOnly, limit: cfg.limit,
      };
      if (cfg.d) params.d = cfg.d;
      if (cfg.prodId) params.prodId = cfg.prodId;

      // 전 기간 집계는 기본 10초 타임아웃을 넘긴다 → 개별 연장
      const opt = { params, timeout: 180000 };
      const [s, b, k, w, g] = await Promise.all([
        axiosInstance.get('/api/v1/quant/micro/summary', opt),
        axiosInstance.get('/api/v1/quant/micro/bins', opt),
        axiosInstance.get('/api/v1/quant/micro/backtest', opt),
        axiosInstance.get('/api/v1/quant/micro/sweep', opt),
        axiosInstance.get('/api/v1/quant/micro/signals', opt),
      ]);
      setSummary(s.data); setBins(b.data || []); setBt(k.data);
      setSweep(w.data || []); setSignals(g.data || []);
      setRan(true);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || '조회 실패');
    } finally { setLoading(false); }
  }, [cfg]);

  // 단조성 차트 — 동시점 vs 시차 대비
  const binChart = useMemo<Highcharts.Options>(() => ({
    chart: { type: 'column', height: 300 },
    title: { text: '신호 구간별 평균수익률 — 동시점 vs 시차', style: { fontSize: '13px', fontWeight: '700' } },
    subtitle: { text: '동시점이 단조 증가하고 시차가 평탄하면 = 예측력 없음 (동어반복)', style: { fontSize: '11px' } },
    xAxis: {
      categories: bins.map((b) => fmt(b.avgSignal, 2)),
      title: { text: 'ti_vol (체결불균형)', style: { fontSize: '11px' } },
      labels: { style: { fontSize: '11px' } },
    },
    // 두 계열의 스케일 차이가 커서(수십 배) 같은 축에 두면 시차가 안 보인다 → 축 분리
    yAxis: [
      {
        title: { text: '동시점 (%)', style: { fontSize: '11px', color: '#64748b' } },
        labels: { style: { fontSize: '11px', color: '#64748b' } },
        plotLines: [{ value: 0, width: 1, color: '#cbd5e1' }],
      },
      {
        title: { text: '시차 (%)', style: { fontSize: '11px', color: '#1e40af' } },
        labels: { style: { fontSize: '11px', color: '#1e40af' } },
        opposite: true,
      },
    ],
    legend: { itemStyle: { fontSize: '11px' } },
    credits: { enabled: false },
    tooltip: { shared: true, valueDecimals: 4, valueSuffix: '%' },
    series: [
      { type: 'column', name: '동시점 (ret_now)', color: '#94a3b8', yAxis: 0, data: bins.map((b) => (b.avgRetNow ?? 0) * 100) },
      { type: 'column', name: '시차 (ret_fwd1)', color: '#1e40af', yAxis: 1, data: bins.map((b) => (b.avgRetFwd ?? 0) * 100) },
    ],
  }), [bins]);

  const sweepCols = useMemo<ColDef<SweepRow>[]>(() => [
    { headerName: '임계값', field: 'thr', width: 90, valueFormatter: (p) => fmt(p.value, 2) },
    { headerName: '보유(분)', field: 'hold', width: 90 },
    { headerName: '트레이드', field: 'trades', width: 100, valueFormatter: (p) => p.value?.toLocaleString() },
    {
      headerName: 'Gross', field: 'grossPct', width: 100, valueFormatter: (p) => fmt(p.value, 4),
      cellStyle: (p) => ({ textAlign: 'right', color: (p.value ?? 0) > 0 ? '#ef4444' : '#3b82f6' }),
    },
    {
      headerName: 'Net', field: 'netPct', width: 100, valueFormatter: (p) => fmt(p.value, 4),
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: (p.value ?? 0) > 0 ? '#ef4444' : '#3b82f6' }),
    },
    { headerName: '적중률', field: 'hitRate', width: 100, valueFormatter: (p) => fmt(p.value, 1) },
    {
      headerName: '비용통과', width: 100,
      valueGetter: (p) => ((p.data?.netPct ?? -1) > 0 ? 'PASS' : 'FAIL'),
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 700, color: p.value === 'PASS' ? '#ef4444' : '#9ca3af' }),
    },
  ], []);

  const signalCols = useMemo<ColDef<SignalRow>[]>(() => [
    {
      headerName: '시각', field: 'bucket', width: 150,
      valueFormatter: (p) => (p.value ? String(p.value).replace('T', ' ').substring(0, 19) : ''),
    },
    { headerName: '종목코드', field: 'prodId', width: 90 },
    { headerName: '종목명', field: 'prodNm', width: 130 },
    { headerName: '가격', field: 'px', width: 90, valueFormatter: (p) => p.value?.toLocaleString(), cellStyle: { textAlign: 'right' } },
    { headerName: '거래량', field: 'totVol', width: 100, valueFormatter: (p) => p.value?.toLocaleString(), cellStyle: { textAlign: 'right' } },
    {
      headerName: 'TI(량)', field: 'tiVol', width: 90, valueFormatter: (p) => fmt(p.value, 3),
      cellStyle: (p) => ({ textAlign: 'right', fontWeight: 700, color: (p.value ?? 0) > 0 ? '#ef4444' : '#3b82f6' }),
    },
    { headerName: 'TI(건수)', field: 'tiCnt', width: 90, valueFormatter: (p) => fmt(p.value, 3), cellStyle: { textAlign: 'right' } },
    { headerName: 'OBI(1단)', field: 'obi1', width: 90, valueFormatter: (p) => fmt(p.value, 3), cellStyle: { textAlign: 'right' } },
    { headerName: 'OBI(가중5)', field: 'obiW5', width: 100, valueFormatter: (p) => fmt(p.value, 3), cellStyle: { textAlign: 'right' } },
    {
      headerName: 'OBI(총)', field: 'obiTot', width: 90, valueFormatter: (p) => fmt(p.value, 3),
      cellStyle: (p) => ({ textAlign: 'right', color: (p.value ?? 0) > 0 ? '#ef4444' : '#3b82f6' }),
    },
    { headerName: '스프레드', field: 'spread', width: 90, valueFormatter: (p) => fmt(p.value, 1), cellStyle: { textAlign: 'right' } },
    {
      headerName: '확증', field: 'confirmed', width: 70,
      valueFormatter: (p) => (p.value === 1 ? 'O' : 'X'),
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 700, color: p.value === 1 ? '#ef4444' : '#9ca3af' }),
    },
    { headerName: '체결수', field: 'fills', width: 80, cellStyle: { textAlign: 'right' } },
    { headerName: '스냅샷', field: 'snaps', width: 80, cellStyle: { textAlign: 'right' } },
    {
      headerName: '익분수익률', field: 'retFwd1', width: 110,
      valueFormatter: (p) => (p.value === null || p.value === undefined ? '-' : (p.value * 100).toFixed(4)),
      cellStyle: (p) => ({ textAlign: 'right', color: (p.value ?? 0) > 0 ? '#ef4444' : '#3b82f6' }),
    },
  ], []);

  // 예측력 판정
  const verdict = useMemo(() => {
    if (!summary || !bt) return null;
    const netOk = (bt.netPct ?? -1) > 0;
    const icOk = Math.abs(summary.icFwd1 ?? 0) > 0.05;
    return { netOk, icOk, pass: netOk && icOk };
  }, [summary, bt]);

  return (
    <div style={{ padding: '12px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* 파라미터 */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={cardTitleStyle}>마이크로구조 신호 (TI/OBI) — kwfilltime + kwdeptime</div>
          <button style={btnStyle} onClick={run} disabled={loading}>
            {loading ? '분석 중...' : '분석 실행'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
          <div>
            <span style={labelStyle}>일자:</span>
            <select style={inputStyle} value={cfg.d} onChange={(e) => setCfg({ ...cfg, d: e.target.value })}>
              <option value="">전체</option>
              {days.map((d) => {
                const ds = d.d.split('T')[0];
                return <option key={ds} value={ds}>{ds} ({d.prods}종목 / {d.buckets.toLocaleString()}버킷)</option>;
              })}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={labelStyle}>종목:</span>
            <StockSearchInput
              value={cfg.prodId}
              onChange={(v: string) => setCfg({ ...cfg, prodId: v })}
              placeholder="종목검색 (빈칸=전체)"
            />
          </div>
          <div>
            <span style={labelStyle}>임계값 |TI|:</span>
            <input style={{ ...inputStyle, width: '60px' }} type="number" step="0.05"
              value={cfg.thr} onChange={(e) => setCfg({ ...cfg, thr: Number(e.target.value) })} />
          </div>
          <div>
            <span style={labelStyle}>보유(분):</span>
            <select style={inputStyle} value={cfg.hold} onChange={(e) => setCfg({ ...cfg, hold: Number(e.target.value) })}>
              <option value={1}>1분</option><option value={3}>3분</option><option value={5}>5분</option>
            </select>
          </div>
          <div>
            <span style={labelStyle}>스프레드 상한(bp):</span>
            <input style={{ ...inputStyle, width: '60px' }} type="number"
              value={cfg.maxSpreadBp} onChange={(e) => setCfg({ ...cfg, maxSpreadBp: Number(e.target.value) })} />
          </div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={cfg.confirmedOnly}
              onChange={(e) => setCfg({ ...cfg, confirmedOnly: e.target.checked })} />
            확증 필터 (체결·호가 동방향)
          </label>
        </div>
      </div>

      {err && (
        <div style={{ ...cardStyle, borderColor: '#fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '12px' }}>
          {err}
        </div>
      )}

      {!ran && !loading && (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#6b7280', fontSize: '13px', padding: '40px' }}>
          파라미터를 설정하고 '분석 실행' 버튼을 클릭하세요. (데이터: kwfilltime + kwdeptime)
        </div>
      )}

      {ran && (
        <>
          {/* 예측력 요약 */}
          <div style={cardStyle}>
            <div style={cardTitleStyle}>예측력 요약 — 동시점 vs 시차 (표본 {summary?.n?.toLocaleString() ?? 0}건)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>IC 동시점</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#94a3b8' }}>{fmt(summary?.icNow)}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>R² {fmt(summary?.r2Now)} — 예측 아님</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>IC 시차 (1분)</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: Math.abs(summary?.icFwd1 ?? 0) > 0.05 ? '#ef4444' : '#3b82f6' }}>
                  {fmt(summary?.icFwd1)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>R² {fmt(summary?.r2Fwd1)} — 실제 예측력</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>IC 시차 (3분)</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(summary?.icFwd3)}</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>IC 시차 (5분)</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(summary?.icFwd5)}</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>IC OBI (시차)</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(summary?.icObiFwd1)}</div>
              </div>
            </div>
            {summary && summary.r2Now !== null && summary.r2Fwd1 !== null && summary.r2Fwd1 > 0 && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#b91c1c', backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' }}>
                동시점 R²가 시차 R²의 <b>{(summary.r2Now / summary.r2Fwd1).toFixed(0)}배</b>입니다.
                동시점 상관은 "가격이 움직일 때 호가도 같이 움직였다"는 동어반복이며 예측이 아닙니다.
              </div>
            )}
          </div>

          {/* 단조성 차트 */}
          <div style={cardStyle}>
            <HighchartsReact highcharts={Highcharts} options={binChart} />
          </div>

          {/* 백테스트 */}
          <div style={cardStyle}>
            <div style={cardTitleStyle}>비용차감 백테스트 (왕복비용 {COST_PCT}% 가정)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>트레이드</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{bt?.trades?.toLocaleString() ?? 0}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>매수 {bt?.longs ?? 0} / 매도 {bt?.shorts ?? 0}</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>Gross (트레이드당)</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: (bt?.grossPct ?? 0) > 0 ? '#ef4444' : '#3b82f6' }}>
                  {fmt(bt?.grossPct)}
                </div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>Net (비용차감)</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: (bt?.netPct ?? 0) > 0 ? '#ef4444' : '#3b82f6' }}>
                  {fmt(bt?.netPct)}
                </div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>적중률</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: (bt?.hitRate ?? 0) >= 50 ? '#ef4444' : '#3b82f6' }}>
                  {fmt(bt?.hitRate, 1)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>50% = 랜덤</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>Sharpe (트레이드당)</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(bt?.sharpe)}</div>
              </div>
              <div style={paramBoxStyle}>
                <div style={paramTitleStyle}>누적 Net</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: (bt?.netTotalPct ?? 0) > 0 ? '#ef4444' : '#3b82f6' }}>
                  {fmt(bt?.netTotalPct, 2)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Gross {fmt(bt?.grossTotalPct, 2)}</div>
              </div>
            </div>
            {verdict && (
              <div style={{
                marginTop: '10px', fontSize: '12px', padding: '8px', borderRadius: '4px', fontWeight: 600,
                backgroundColor: verdict.pass ? '#f0fdf4' : '#fef2f2',
                color: verdict.pass ? '#15803d' : '#b91c1c',
              }}>
                {verdict.pass
                  ? '판정: 비용 차감 후에도 양(+)의 기대수익. 추가 검증(walk-forward) 진행 가치 있음.'
                  : `판정: 비용 차감 후 손실. 평균 기대수익이 왕복비용 ${COST_PCT}%를 넘지 못합니다. 실거래 투입 부적합.`}
              </div>
            )}
          </div>

          {/* 파라미터 스윕 */}
          <div style={cardStyle}>
            <div style={cardTitleStyle}>파라미터 스윕 — 임계값 × 보유기간 ({sweep.length}개 조합)</div>
            <div className="ag-theme-quartz" style={{ height: '360px', width: '100%' }} {...copyMenu}>
              <AgGridReact<SweepRow>
                rowData={sweep} columnDefs={sweepCols}
                defaultColDef={{ sortable: true, resizable: true }}
                headerHeight={30} rowHeight={26}
              />
            </div>
            <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
              '비용통과' 열이 전부 FAIL 이면 파라미터 튜닝으로 해결되지 않는 구조적 문제입니다.
            </div>
          </div>

          {/* 신호 목록 */}
          <div style={cardStyle}>
            <div style={cardTitleStyle}>1분 버킷 신호 목록 ({signals.length.toLocaleString()}건)</div>
            <div className="ag-theme-quartz" style={{ height: '460px', width: '100%' }} {...copyMenu}>
              <AgGridReact<SignalRow>
                rowData={signals} columnDefs={signalCols}
                defaultColDef={{ sortable: true, resizable: true, filter: true }}
                headerHeight={30} rowHeight={26} pagination paginationPageSize={100}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuantMicroSignal;
